import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 天然气制油（GTL）P&ID 详细图：蒸汽转化 → F-T 合成 → 加氢裂化 → 分馏 */
const media = {
  gas: { label: '天然气', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  syngas: { label: '合成气', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  oil: { label: 'F-T 合成油 / 柴油', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  h2: { label: '氢气', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
};

const devices = [
  { id: 'f101', kind: 'furnace' as const, x: 120, y: 146, tag: 'F-101', name: '蒸汽转化炉', note: '850℃' },
  { id: 'r101', kind: 'reactor' as const, x: 320, y: 128, tag: 'R-101', name: 'F-T合成塔', note: '列管式' },
  { id: 'v101', kind: 'vDrum' as const, x: 500, y: 161, tag: 'V-101', name: 'F-T油分离' },
  { id: 'r102', kind: 'reactor' as const, x: 670, y: 128, tag: 'R-102', name: '加氢裂化' },
  { id: 'v102', kind: 'vDrum' as const, x: 850, y: 154, tag: 'V-102', name: '高压分离器' },
  { id: 't101', kind: 'column' as const, x: 1010, y: 101, tag: 'T-101', name: '产品分馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-gasin', label: '天然气', medium: 'gas', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-stm', label: '蒸汽', medium: 'steam', x: 60, y: 40, direction: 'in' as const },
  { id: 'p-h2', label: '新氢', medium: 'h2', x: 620, y: 40, direction: 'in' as const },
  { id: 'p-naphtha', label: '石脑油', medium: 'oil', x: 1190, y: 52, direction: 'out' as const },
  { id: 'p-diesel', label: '柴油', medium: 'oil', x: 1190, y: 611, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-gasin', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'gas' },
  { id: 's02', from: 'p-stm', fromSide: 'right', to: 'f101', toSide: 'top', medium: 'steam' },
  { id: 's03', from: 'f101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'syngas' },
  { id: 's04', from: 'r101', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's05', from: 'v101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'oil' },
  { id: 's06', from: 'p-h2', fromSide: 'right', to: 'r102', toSide: 'top', medium: 'h2' },
  { id: 's07', from: 'r102', fromSide: 'right', to: 'v102', toSide: 'left', medium: 'oil' },
  { id: 's08', from: 'v102', fromSide: 'right', to: 't101', toSide: 'left', medium: 'oil' },
  { id: 's09', from: 't101', fromSide: 'top', to: 'p-naphtha', toSide: 'left', medium: 'oil' },
  { id: 's10', from: 't101', fromSide: 'bottom', to: 'p-diesel', toSide: 'left', medium: 'oil' },
];

const GtlDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="GTL 装置详细流程"
    subtitle="天然气蒸汽转化 → 合成气 → 费托（F-T）合成 → F-T 油分离 → 加氢裂化改质 → 分馏出柴油/石脑油（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default GtlDiagram;
